import { ActionType } from '../../../../core/redux/actionType';
import { DirectoryRater } from '../models';
import { Endpoints } from '../../../../core/api/endpoints';
import { makeRsaaTypes, makeApiAction, ApiReducer, makeApiReducer } from '../../../../core/api/rsaa';
import { AppState } from '../../../../core/redux/appState';
import { getLoading, addLoading, addError, noLoadingNoError } from '../../../../core/api/module';
import * as R from 'ramda';


const KEY_ACTION = ActionType.DirectoryRatersRequest;
const ACTIONS    = [
  ActionType.DirectoryRatersRequest,
  ActionType.DirectoryRatersResponse,
  ActionType.DirectoryRatersError,
];


type Payload = DirectoryRater[];


// TODO: determine if we need to store with this level of granularity; if so, we should probably hash the search term
type Meta = {
  projectId: number;
  participantId: number;
  keyword: string;
}


/**
 * Dispatch this action to search for prospective raters by keyword.
 *
 * @param projectId - the database Id for the project for which we're retrieving raters
 * @param participantId - the person Id for the user for whom raters are being searched
 * @param keyword - the search term used to find one or more raters
 */
export const directoryRatersAction = (
  projectId: number,
  participantId: number,
  keyword: string,
) => makeApiAction<Payload, Meta>(
  {
    method:   'POST',
    endpoint: Endpoints.DirectoryRaters,
    headers:  { 'content-type': 'application/json' },
    body:     JSON.stringify({
      projectId:     projectId,
      participantId: participantId,
      keyword:       keyword,
    }),
    types:    makeRsaaTypes<Payload, Meta>(ACTIONS, { projectId, participantId, keyword }),
    bailout:  (state: AppState) => !!getLoading(
      state,
      KEY_ACTION,
      `${projectId}-${participantId}-${keyword.toLocaleLowerCase(state.locale.lang.code)}`,
    ),
  },
);

const reducer: ApiReducer<Payload, Meta> = (state, action, errorMsg, isLoading, payload) => {
  const lowercaseSearchTerm = action.meta.keyword.toLocaleLowerCase(state.locale.lang.code);

  const metaTag = `${action.meta.projectId}-${action.meta.participantId}-${lowercaseSearchTerm}`;

  if (isLoading) {
    return addLoading(state, KEY_ACTION, metaTag);
  }

  if (errorMsg) {
    return addError(state, KEY_ACTION, errorMsg, metaTag);
  }

  const successState = noLoadingNoError(state, KEY_ACTION, metaTag);

  return R.assocPath(
    ['directoryRaters', `${action.meta.projectId}`, `${lowercaseSearchTerm}`],
    payload,
    successState,
  );
};

export const directoryRatersReducer = makeApiReducer<Payload, Meta>(ACTIONS, KEY_ACTION, reducer);
